import { DfExternalProductAttr, DfProductVersionIdentifier, DfUcProgram } from "../../shared-logic/utils/dfConstants";
import { getGradeName, getEngineName } from "../../shared-logic/utils/uscUtils";
import { defaultFinanceInfoObject } from "../../../common-deprecated/constants";
import { ExternalPriceKey, getExternalPriceExtraCostValue, getFeeListSelector } from "./externalPriceUtils";
import { getTotalPriceSelector } from "./priceUtils";
import { getUscCashPriceValue } from "../../shared-logic/utils/uscPriceUtils";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import { formatPriceIntl } from "../../../common-deprecated/priceUtils";
import {
    type VehicleType,
    type ProductVersionIdentifierType,
    type UscFinanceInfoType,
    UscContext,
} from "../../shared-logic/types/UscCommonTypes";
import { getUscLabel } from "./labels";
import type { UscStateType } from "../redux/store";
import type { UscFinanceDataSource } from "./constants";
import { findProductVersion } from "./helpers";
import { getAccessoryPackPriceSelector } from "./accessoryUtils";
import { isAccessoryInBasketSelector, isPackInBasketSelector } from "./basketUtils";
import { sanitize } from "../../../common-deprecated/utils/sanitizer";

// ----------------------------------------------------------------------
// Finance types
// ----------------------------------------------------------------------

// Base type used to format a finance api response into (both used and stock)
export type FormattedUscFinanceResponseType = {
    monthlyPrice: number;
    financeInfo: UscFinanceInfoType;
    accessories: UscFinanceAccessoryType[];
    services: UscFinanceServiceType[];
    costs: UscFinanceCostType[];
};

export type UscFinanceResponseDebugType =
    | { source: UscFinanceDataSource.ApiUsed; data: UsedFinanceApiReturnType }
    | { source: UscFinanceDataSource.ApiStock; data: StockFinanceApiReturnType }
    | { source: UscFinanceDataSource.Event; data: FinanceCalculatorEventObject }
    | { source: UscFinanceDataSource.Df; data: unknown };

// Selected finance service.
export type UscFinanceServiceType = {
    label: string;
    monthlyPrice: number;
    monthlyPriceFormatted: string;
    includedInFinance: boolean; // Indicates the service is included by default in the finance plan at no additional cost
    selected: boolean; // Indicates the user has selected this service to be added to the finance plan (usually includes an additional cost)
};

// Monthly price for a "cost" which usually translates to a cost coming from TMEF.
export type UscFinanceCostType = { externalPriceCode: string; monthlyPrice: number; monthlyPriceFormatted: string };

// Monthly price of an accessory.
export type UscFinanceAccessoryType = {
    accessoryId: string;
    monthlyPrice: number;
    monthlyPriceFormatted: string;
};

// Costs as returned from finance service.
export type UsedFinanceApiCostsType = Partial<{
    amount: number;
    code: string;
    monthlyPayment: number;
    monthlyPaymentFormatted: string;
    taxDefCode: string;
    selected: boolean;
}>;

// Services returned from finance service.
export type UsedFinanceApiServicesType = Partial<{
    amount: number;
    amountFormatted: string;
    category: string;
    code: string;
    includedInFinance: boolean;
    name: string;
    selected: boolean;
}>;

export type UsedFinanceApiAccessoriesType = {
    amount: number;
    monthlyPayment: number;
    monthlyPaymentFormatted: string;
    name: string;
    code: string;
    selected: boolean;
};

// Return type from used cars finance API (/used-cars/api/monthly-payment)
export type UsedFinanceApiReturnType = {
    // No specific documentation available, but it looks like some properties are optional and some arent.
    // Assume everything is optional to be safe.
    financial: Partial<{
        monthlyPayment: {
            value: string;
            formatted: string;
        };
        // Not a typo, downpayment is not camel cased and monthlyPayment is.
        downpayment: {
            value: string;
            formatted: string;
        };
        apr: {
            value: string;
            formatted: string;
        };
        tin: {
            value: string;
            formatted: string;
        };
        financeDiscount: {
            value: string;
            formatted: string;
        };
        financeBonus: {
            value: string;
            formatted: string;
        };
        finalPrice: {
            value: string;
            formatted: string;
        };
        financialScale: string;
        productCode: string;
        legalText: string;
        financingType: string;
        tarification: string;
        validityDate: string;
        additionalServices: string[];
        duration: {
            value: string;
            formatted: string;
        };
        guaranteedFutureValue: { value: string; formatted: string };
        contractMileage: { value: string; formatted: string };
        installmentsNumber: { value: string; formatted: string };
        effectiveInterestRate: { value: string; formatted: string };
        interestRate: { value: string; formatted: string };
        // TBA Estonia
        margin: { value: string; formatted: string };
        euriborMonths: { value: string; formatted: string };
        euriborDate: { value: string; formatted: string };
        euribor: { value: string; formatted: string };
        // TBEL
        lastInstalment: { value: string; formatted: string };
        productName: { value: string; formatted: string };
        // TMI (equivalent to financingType)
        productDescription: {
            value: string;
            formatted: string;
        };
        // TAT
        productType: string;
        contractFee: { value: string; formatted: string };
        nominalInterestRate: { value: string; formatted: string };
        residualValue: { value: string; formatted: string };
        amountFinanced: { value: string; formatted: string };
        totalCostOfCredit: { value: string; formatted: string };
    }>;
    services?: UsedFinanceApiServicesType[];
    costs?: UsedFinanceApiCostsType[];
    accessories?: UsedFinanceApiAccessoriesType[];
};

export type StockFinanceApiReturnType = {
    financial: Partial<{
        monthlyPayment: { value: string; formatted: string };
        downpayment: { value: string; formatted: string };
        contractMileage: { value: string; formatted: string };
        legalText: string;
        additionalServices: string[];
    }>;
};

export type FinanceCalculatorEventObject = {
    apr: string;
    downpayment: string;
    duration?: string;
    financeQuoteId: string;
    guaranteedFutureValue?: string;
    legalText: string;
    monthlyPayment: string;
    productCode: string;
    accessories?: UsedFinanceApiAccessoriesType[];
    services?: UsedFinanceApiServicesType[];
    costs?: UsedFinanceApiCostsType[];
};

// Finance data which is saved in session
export type UscFinanceQuoteSessionType = {
    financeQuoteId?: string;
};

// Payload for used finance/insurance endpoints.
// When updating this, make sure the table at https://cde.toyota-europe.com/confluence/pages/viewpage.action?pageId=182167918 is up to date
export type UsedFinanceInsurancePayload = {
    country: string;
    language: string;
    elementType: string;
    brand: "toyota" | "lexus";
    vehicle: {
        // Shared
        vehicleForSaleId: string;
        mileage: number;
        version: string;
        model: string;
        modelYear: string;
        body: string;
        vat: string;
        make: string;
        vehicleType: string;
        sellingPrice: number;
        originalRegistrationDate: number;
        vin: string;

        // TES
        eurotaxRetailPrice: number;
        eurotaxRetailPriceDate: number;
        vehicleSource?: string;
        versionCode: string;
        category: boolean;

        // TFR
        channel: "ONLINE"; // Hardcoded?
        customerType: "PERSON";
        modelName: string;
        brandName: string;
        msrpPrice: number;
        warrantyCode: string;
        engine: string;
        grade: string;
        fuelType: string;
        bodyType: string;
        powerKw: number;
        powerHp: number;
        licensePlate: string;
        gearBox: string;

        // TDG
        doors: number;

        // Additional prices
        costs?: {
            amount: number;
            code: string;
            name: string;
            selected: boolean;
            taxDefCode?: string;
        }[];

        // The accessories and services properties are based on (sometimes inaccurate) Swagger docs and have not been properly tested yet.
        // Also don't know what the mapping should be for these values, guess we'll figure this out later...
        services?: {
            amount: number;
            category: string;
            code: string;
            name: string;
            selected: boolean;
            promotions?: {
                amount: number;
                category: string;
                code: string;
                name: string;
                taxIncluded: boolean;
            }[];
        }[];
        accessories?: {
            amount: number;
            code: string; // This is not actually used yet at Makolabs side, they added this as they expected this to be needed at some point.
            category: string; // This is required for TES.
            name: string;
            selected: boolean;
        }[];
    };
    dealer: { dealerCode: string; deliveryCost: number };
    parameters?: { financeQuoteId?: string; insuranceQuoteId?: string };
};

export type UsedFinanceInsurancePayloadV2 = {
    country: string;
    language: string;
    elementType: string;
    brand: "toyota" | "lexus";
    vehicle: {
        vehicleForSaleId: string;
    };
    parameters?: { financeQuoteId?: string; insuranceQuoteId?: string };
};

// Payload used for stock finance endpoints.
export type StockFinancePayload = {
    country: string;
    language: string;
    elementType: "stockCarsSimulator";
    brand: "toyota" | "lexus";
    vehicle: {
        vehicleForSaleId: string;
        make: string;
        localCode: string;
        sellingPrice: number;
        katashiki: string;
        suffix: string;
        colourCode: string;
    };
    dealer: {
        dealerCode: string;
    };
    parameters?: { financeQuoteId?: string; insuranceQuoteId?: string };
};

/**
 * Get a payload used for finance/insurance calculations.
 * See https://cde.toyota-europe.com/confluence/pages/viewpage.action?pageId=182167918 for documentation about field mapping.
 */
export const getFinanceInsurancePayloadSelector =
    (requestType: "finance" | "insurance") =>
    (state: UscStateType): UsedFinanceInsurancePayload | UsedFinanceInsurancePayloadV2 | StockFinancePayload | null => {
        const { vehicle } = state;

        if (vehicle.type === UscContext.Used) return usedFinanceInsurancePayloadSelector(requestType, state);
        if (vehicle.type === UscContext.Stock) return stockFinancePayloadSelector(state);

        return null;
    };

/**
 * Minor helper function for easily retrieving externalProductAttributes.
 */
const getExternalAttrValue = (vehicleResult: VehicleType, attribute: DfExternalProductAttr): string =>
    vehicleResult.product.externalProductAttributes.find((extAttr) => extAttr.code === attribute)?.value || "";

/**
 * Returns the version code with some country specific logic
 */
export const getVersionCode = (vehicleResult: VehicleType, commonSettings: CommonSettingsType): string | undefined => {
    const { language } = commonSettings;

    let productVersion: ProductVersionIdentifierType | undefined;

    // This map makes it easier to add new countries in the future.
    // The order of the product version identifiers (if the country has multiple possible fallbacks) is important,
    // as the first one that matches will be used.
    const languageToProductVersionMap: Record<string, DfProductVersionIdentifier[]> = {
        it: [DfProductVersionIdentifier.A2P_LMC],
        fr: [DfProductVersionIdentifier.ARG_VRS_NO],
        es: [
            DfProductVersionIdentifier.A2P_LMC,
            DfProductVersionIdentifier.A2P_LMC_SFX,
            DfProductVersionIdentifier.ETG_NVC,
        ],
        de: [DfProductVersionIdentifier.KBA],
    };

    if (language in languageToProductVersionMap) {
        // Country specific logic
        // For each language, try to find the first product version identifier that matches.
        // If none is found, fall back to the default logic.
        languageToProductVersionMap[language].forEach((productVersionIdentifier) => {
            if (!productVersion) productVersion = findProductVersion(vehicleResult, productVersionIdentifier);
        });
    }

    if (productVersion) return productVersion.versionCode;

    // As described by Makolab Insurance, see OR-3899.
    // If the specific code is not found, fall back to the first one as for some reason Makolab finance always expects a version...
    return vehicleResult.product.productVersionIdentifiers[0]?.versionCode;
};

const getRequestObjectParamaters = ({
    finance,
    insurance,
}: UscStateType): { financeQuoteId?: string; insuranceQuoteId?: string } => {
    return {
        ...(finance.financeQuoteId ? { financeQuoteId: finance.financeQuoteId } : {}),
        ...(insurance.insuranceQuoteId ? { insuranceQuoteId: insurance.insuranceQuoteId } : {}),
    };
};

const usedFinanceInsurancePayloadSelector = (
    requestType: "finance" | "insurance",
    state: UscStateType,
): UsedFinanceInsurancePayload | UsedFinanceInsurancePayloadV2 | null => {
    const { commonSettings, vehicle, externalPrice, uscSettings, accessories } = state;
    const { country, language } = commonSettings;

    // TDK uses a new way of calculating the cash price, so we need to use a different payload.
    if (country.toLowerCase() === "dk") return usedFinanceInsurancePayloadSelectorV2(state);

    const { cashPrice } = getTotalPriceSelector()(state);
    const finalCashPrice = getUscCashPriceValue(cashPrice);

    if (!vehicle.result) return null;

    const vehicleResult = vehicle.result;

    const includedFeeList = getFeeListSelector("included")(state);
    const deliveryCost =
        includedFeeList.find((fee) => fee.key === ExternalPriceKey.DeliveryCost)?.cashPrice ||
        vehicleResult.vehicleValue.deliveryCost ||
        0;

    // TAF Used sellingPrice edge case:
    // The sellingPrice property should not contain the deliveryCost even though the OR totalPrice includes it.
    // This is because the monthly rate shown on the filters and pdp are the same, but the cash price shown isnt.
    // (Filter shows cash price without deliveryFee, pdp shows cash price with deliveryFee)
    // As we still want the OR totalPrice logic to remain consistent we are removing the deliveryCost here instead of tweaking the totalPrice logic.
    const sellingPrice =
        commonSettings.country === "fi" && vehicle.type === UscContext.Used
            ? finalCashPrice - deliveryCost
            : finalCashPrice;

    const requestObject: UsedFinanceInsurancePayload = {
        country,
        language,
        elementType: "usedCarsSimulator",
        brand: commonSettings.brand,
        vehicle: {
            vehicleForSaleId: vehicle.vehicleForSaleId,
            mileage: vehicleResult.mileage?.value || 0,
            version: vehicleResult.product.versionName,
            model: getExternalAttrValue(vehicleResult, DfExternalProductAttr.ModelCode),
            modelYear: vehicleResult.product.modelYear,
            body: getExternalAttrValue(vehicleResult, DfExternalProductAttr.BodyTypeCode),
            make: getExternalAttrValue(vehicleResult, DfExternalProductAttr.BrandCode),
            vehicleType: getExternalAttrValue(vehicleResult, DfExternalProductAttr.CarTypeCode),
            sellingPrice,
            originalRegistrationDate: vehicleResult.history?.registrationDate
                ? new Date(vehicleResult.history.registrationDate).getTime()
                : 0,
            vin: vehicleResult.vin,
            versionCode: getVersionCode(vehicleResult, commonSettings) || "",

            // TES
            eurotaxRetailPrice: vehicleResult.vehicleValue.marketPrice || 0,
            eurotaxRetailPriceDate: vehicleResult.vehicleValue.valuationDate
                ? new Date(vehicleResult.vehicleValue.valuationDate).getTime()
                : 0,
            vehicleSource: vehicleResult.sourceCode,
            category: [
                DfUcProgram.ToyotaPlus,
                DfUcProgram.ToyotaApprovedUse,
                DfUcProgram.LexusSelect,
                DfUcProgram.LexusPremiumUsed,
            ].includes(vehicleResult.ucProgram.code as DfUcProgram),

            // TFR
            vat: vehicleResult?.price?.reclaimableVAT ? "2" : "0", // Value used for TFR finance calculations (not sure if relevant for anyone else).
            channel: "ONLINE", // Hardcoded?
            customerType: "PERSON",
            modelName: vehicleResult.product.model,
            brandName: vehicleResult.product.brand.description,
            msrpPrice: vehicleResult.vehicleValue.cataloguePrice || 0,
            warrantyCode: vehicleResult.warranty?.code || "",
            engine: getEngineName(vehicleResult),
            grade: getGradeName(vehicleResult),
            fuelType: vehicleResult.product.engine.fuelType.description,
            bodyType: vehicleResult.product.bodyType,
            // Both KW and HP are used here for data-quality reasons even though they can be inferred from each other.
            // In theory only powerHp will be used after a transition period, but keep both anyway just in case.
            powerKw: vehicleResult.product.engine.powerOutputKilowatts?.value || 0,
            powerHp: vehicleResult.product.engine.powerOutputHorsepower?.value || 0,
            licensePlate: vehicleResult.licensePlate,
            gearBox: vehicleResult.product.transmission.transmissionType?.code || "",

            // TDG
            doors: vehicleResult.product.doors,
        },
        dealer: { dealerCode: vehicleResult.dealerLocalId, deliveryCost },
    };

    // TFR valuation crashes when adding this field 🙄
    if (country !== "es") delete requestObject.vehicle.vehicleSource;

    // Add the finance/insurance quote ids if available.
    requestObject.parameters = getRequestObjectParamaters(state);

    // Only used supports financing for externalPrice values for now.
    if (requestType === "finance" && vehicle.type === UscContext.Used) {
        // Add relevant costs if available and shown to the end user.
        if (externalPrice.data?.ExtraCosts?.length && externalPrice.show) {
            // Only add the costs that are included in the total price as the total price is used to calculate the monthly rate.
            requestObject.vehicle.costs =
                externalPrice.data?.ExtraCosts.filter(
                    (cost) => getExternalPriceExtraCostValue(cost)?.IncludedInPrice,
                ).map((cost) => ({
                    name: cost.Key,
                    amount: getExternalPriceExtraCostValue(cost)!.Amount,
                    code: cost.EposCode || "",
                    taxDefCode: typeof cost.EposVatCode !== "undefined" ? String(cost.EposVatCode) : undefined,
                    selected: cost?.EposSelectionIndicator === "YES",
                })) || [];
        }
    }

    // Add accessories and packs with a price to the payload.
    const { accessoryResults, packResults } = accessories;
    const hasPackOrAccessories = !!(accessoryResults.length || packResults.length);
    if (requestType === "finance" && hasPackOrAccessories && !uscSettings.disableAccessoryMonthlyPrice) {
        const mappedAccessories = accessoryResults.map((accessory) => ({
            code: accessory.id,
            selected: isAccessoryInBasketSelector(accessory.id)(state),
            category: "ACCESSORY",
            amount: getAccessoryPackPriceSelector(accessory)(state)?.cashPrice.cashPriceInclVat || 0,
            name: accessory.name,
        }));

        const mappedPacks = packResults.map((pack) => ({
            code: pack.id,
            selected: isPackInBasketSelector(pack.id)(state),
            category: "PACK",
            amount: getAccessoryPackPriceSelector(pack)(state)?.cashPrice.cashPriceInclVat || 0,
            name: pack.name,
        }));

        requestObject.vehicle.accessories = [...mappedAccessories, ...mappedPacks].filter(
            (accessoryPack) => !!accessoryPack.amount,
        ); // It doesn't make sense to send accessories/packs with no price, so filter these.
    }

    return requestObject;
};

// Makolab has changed the way we should handle the opening of the finance modal.
// We no longer build the payload, and instead just send the vehicleForSaleId - Makolab
// fetches the vehicle data from the backend and builds the payload there.
// To make it clear that this is a new way of doing things, I'm making this a completely
// new selector instead of modifying the old one.
export const usedFinanceInsurancePayloadSelectorV2 = (state: UscStateType): UsedFinanceInsurancePayloadV2 | null => {
    const { commonSettings, vehicle } = state;
    const { country, language, brand } = commonSettings;

    if (!vehicle.result) return null;

    const requestObject: UsedFinanceInsurancePayloadV2 = {
        country,
        language,
        elementType: "usedCarsSimulator",
        brand,
        vehicle: {
            vehicleForSaleId: vehicle.vehicleForSaleId,
        },
    };

    requestObject.parameters = getRequestObjectParamaters(state);

    return requestObject;
};

const stockFinancePayloadSelector = (state: UscStateType): StockFinancePayload | null => {
    const { commonSettings, vehicle } = state;
    const { country, language } = commonSettings;
    const { cashPrice } = getTotalPriceSelector()(state);
    const finalCashPrice = getUscCashPriceValue(cashPrice);

    if (!vehicle.result) return null;

    const vehicleResult = vehicle.result;

    const localCode =
        vehicle.result.product.productVersionIdentifiers.find(
            (identifier) => identifier.versionType.code === "MKT_VW_LOCAL_CODE",
        )?.versionCode || "";

    const requestObject: StockFinancePayload = {
        country,
        language,
        elementType: "stockCarsSimulator",
        brand: commonSettings.brand,
        vehicle: {
            vehicleForSaleId: vehicle.vehicleForSaleId,
            sellingPrice: finalCashPrice,
            localCode,
            colourCode: getExternalAttrValue(vehicleResult, DfExternalProductAttr.ExteriorColourCode),
            // The used finance payload uses an externalAttribute for this but it doesn't look like this is available for stock cars.
            // Use toyota/lexus as a default.
            make: commonSettings.brand,
            katashiki: vehicleResult.product.katashiki?.fullKatashiki || "",
            suffix: vehicleResult.product.orderableSuffix?.suffixCode || "",
        },
        dealer: {
            dealerCode: vehicleResult.dealerLocalId,
        },
    };

    requestObject.parameters = getRequestObjectParamaters(state);

    return requestObject;
};

/**
 * Format the response from the used finance api into a consistent response to be used across the USC component.
 */
export const formatUsedFinanceApiResponse = (result: UsedFinanceApiReturnType): FormattedUscFinanceResponseType => {
    const services: UscFinanceServiceType[] = result.services
        ? result.services.map((service) => ({
              label: service.name || "",
              includedInFinance: Boolean(service.includedInFinance),
              monthlyPrice: Number(service.amount),
              monthlyPriceFormatted: service.amountFormatted || "",
              selected: service.selected || false,
          }))
        : [];

    const costs: UscFinanceCostType[] = result.costs
        ? result.costs.map((cost) => ({
              externalPriceCode: cost.code || "",
              monthlyPrice: cost.monthlyPayment || 0,
              monthlyPriceFormatted: cost.monthlyPaymentFormatted || "",
          }))
        : [];

    const accessories: UscFinanceAccessoryType[] = result.accessories
        ? result.accessories.map((accessory) => ({
              monthlyPrice: accessory.monthlyPayment,
              monthlyPriceFormatted: accessory.monthlyPaymentFormatted,
              accessoryId: accessory.code,
          }))
        : [];

    return {
        monthlyPrice: Number(result.financial.monthlyPayment?.value),
        financeInfo: {
            ...defaultFinanceInfoObject,
            disclaimer: { value: result.financial?.legalText ? sanitize(result.financial?.legalText) : "" },
            annualInterestRate: result.financial.apr?.value || result.financial?.interestRate?.value || "",
            effectiveInterestRate: result.financial?.effectiveInterestRate?.value || "",
            downPayment: result.financial.downpayment?.value || "",
            term: result.financial.duration?.value || result.financial?.installmentsNumber?.value || "",
            residualValue: result.financial.guaranteedFutureValue?.value || result.financial.residualValue?.value || "",
            lastInstalment: result.financial.lastInstalment?.value || "",
            // Some countries (such as TBEL) use "productName", other (such as TFR) use "financingType"
            // New cars uses "productName", so we map it to productName to at least try to achieve some consistency at our side.
            productName: result.financial?.financingType || result.financial?.productName?.value || "",
            margin: result.financial?.margin?.value || "",
            euriborDate: result.financial?.euriborDate?.value || "",
            euriborMonths: result.financial?.euriborMonths?.value || "",
            euriborValue: result.financial?.euribor?.value || "",
            productType: result.financial?.productType || "",
            contractFee: result.financial?.contractFee?.value || "",
            nominalInterestRate: result.financial?.nominalInterestRate?.value || "",
            amountFinanced: result.financial?.amountFinanced?.value || "",
            totalCostOfCredit: result.financial?.totalCostOfCredit?.value || "",
            installments: result.financial?.installmentsNumber?.value || "",
        },
        accessories,
        services,
        costs,
    };
};

/**
 * Format a stock finance response into the FormattedUscFinanceResponseType used in the USC components.
 */
export const formatStockFinanceApiResponse = (result: StockFinanceApiReturnType): FormattedUscFinanceResponseType => {
    // The stock response also contains a new 'contractMileage' property.
    // For now this is unsupported but chances are well need to implement it at some point...
    return {
        monthlyPrice: Number(result.financial.monthlyPayment?.value),
        financeInfo: {
            ...defaultFinanceInfoObject,
            disclaimer: { value: result.financial?.legalText ? sanitize(result.financial?.legalText) : "" },
            downPayment: result.financial.downpayment?.value || "",
        },
        accessories: [],
        services: [],
        costs: [],
    };
};

/**
 * Type for formatted finance service.
 */
export type FormattedFinanceServiceType = {
    formattedPrice: string;
    label: string;
    key: string;
    includedInFinance?: boolean;
};

/**
 * Retrieve a formatted list of Makolab finance services.
 */
export const financeServicesSelector = (state: UscStateType): FormattedFinanceServiceType[] => {
    const { basket, finance, commonSettings } = state;
    const [monthlyFormatLabel, includedLabel] = getUscLabel(state, [
        "uscDefaultMonthlyRateFormat",
        "uscBasketServicesIncluded",
    ]);
    if (basket.activeFinanceOption === "cash" || finance.services.length === 0) return [];

    return (
        finance.services
            // A service should be shown if it is either included in the finance plan or selected by the user.
            .filter((service) => service.includedInFinance || service.selected)
            .map((financeService) => ({
                key: financeService.label,
                label: financeService.label,
                includedInFinance: financeService.includedInFinance,
                formattedPrice: financeService.includedInFinance
                    ? includedLabel
                    : monthlyFormatLabel.replace(
                          "{price}",
                          formatPriceIntl(commonSettings, financeService.monthlyPrice).primaryPrice,
                      ),
            }))
    );
};
